import { useState, useCallback } from 'react';
import { collections as mockCollections } from '../../../mocks/altaCostura';
import { useSupabaseCollections } from '../../../hooks/useSupabaseCollections';
import type { AltaCosturaCollection } from '../../../hooks/useSupabaseCollections';

const PAGE_SIZE = 10;

export default function AdminPortfolio() {
  const { data, add, update, remove, updateOrder, loading, refresh } =
    useSupabaseCollections(
      mockCollections.map((c, i) => ({ ...c, sort_order: i }))
    );
  const [editing, setEditing] = useState<AltaCosturaCollection | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);

  const filtered = data.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.year.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const paged = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  const handleSave = (formData: FormData) => {
    setFormError(null);
    const name = (formData.get('name') as string)?.trim();
    const year = (formData.get('year') as string)?.trim();
    const description = (formData.get('description') as string)?.trim();
    const image = (formData.get('image') as string)?.trim();
    const imagesRaw = (formData.get('images') as string)?.trim();
    const featured = formData.get('featured') === 'on';

    if (!name || !year || !image) {
      setFormError('Nombre, año e imagen principal son obligatorios.');
      return;
    }

    const images = imagesRaw
      ? imagesRaw
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

    const payload: Omit<AltaCosturaCollection, 'id'> = {
      name,
      year,
      description,
      featured,
      image,
      images,
      sort_order: editing?.sort_order ?? data.length,
    };

    if (editing) {
      update(editing.id, payload);
      setEditing(null);
    } else {
      add(payload);
      setIsAdding(false);
    }
  };

  const moveItem = useCallback(
    (fromId: number, toId: number) => {
      if (fromId === toId) return;
      const orderedIds = data.map((d) => d.id);
      const fromIndex = orderedIds.indexOf(fromId);
      const toIndex = orderedIds.indexOf(toId);
      if (fromIndex === -1 || toIndex === -1) return;
      orderedIds.splice(fromIndex, 1);
      orderedIds.splice(toIndex, 0, fromId);
      updateOrder(orderedIds);
    },
    [data, updateOrder]
  );

  const handleDragStart = (
    e: React.DragEvent<HTMLTableRowElement>,
    id: number
  ) => {
    setDraggingId(id);
    e.dataTransfer.setData('text/plain', String(id));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLTableRowElement>,
    id: number
  ) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverId !== id) setDragOverId(id);
  };

  const handleDrop = (
    e: React.DragEvent<HTMLTableRowElement>,
    toId: number
  ) => {
    e.preventDefault();
    const fromId = Number(e.dataTransfer.getData('text/plain'));
    moveItem(fromId, toId);
    setDragOverId(null);
    setDraggingId(null);
  };

  const handleDragEnd = () => {
    setDragOverId(null);
    setDraggingId(null);
  };

  return (
    <div className="space-y-6">
      {loading && (
        <div className="flex items-center gap-2 text-brand-gray-500 text-sm bg-brand-gray-50 rounded-lg px-4 py-3">
          <i className="ri-loader-4-line animate-spin" />
          <span>Sincronizando con la nube…</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="font-serif text-brand-black text-2xl font-medium">
          Portafolio{' '}
          <span className="text-brand-gray-400">({data.length})</span>
        </h2>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Buscar colección..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full sm:w-64 bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
          />
          <button
            onClick={() => {
              setIsAdding(true);
              setEditing(null);
              setFormError(null);
            }}
            className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
          >
            + Nueva colección
          </button>
          <button
            onClick={refresh}
            className="w-10 h-10 rounded-lg bg-white border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:text-brand-black hover:border-brand-black transition-colors"
            title="Recargar"
          >
            <i className="ri-refresh-line" />
          </button>
        </div>
      </div>

      {/* Formulario */}
      {(isAdding || editing) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave(new FormData(e.currentTarget));
          }}
          className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-5"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-brand-black text-lg">
              {editing ? 'Editar Colección' : 'Nueva Colección'}
            </h3>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setIsAdding(false);
                setFormError(null);
              }}
              className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 mt-0.5" />
              <p className="font-sans text-red-600 text-sm">{formError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Nombre *
              </label>
              <input
                name="name"
                defaultValue={editing?.name ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Año *
              </label>
              <input
                name="year"
                defaultValue={editing?.year ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
          </div>

          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Descripción
            </label>
            <textarea
              name="description"
              defaultValue={editing?.description ?? ''}
              rows={3}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Imagen principal (URL) *
              </label>
              <input
                name="image"
                defaultValue={editing?.image ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Galería (una URL por línea)
              </label>
              <textarea
                name="images"
                defaultValue={editing?.images?.join('\n') ?? ''}
                rows={3}
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="featured"
              name="featured"
              defaultChecked={editing?.featured ?? false}
              className="w-4 h-4 rounded border-brand-gray-300 text-brand-black focus:ring-brand-black"
            />
            <label
              htmlFor="featured"
              className="font-sans text-brand-gray-600 text-sm"
            >
              Marcar como colección destacada
            </label>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
            >
              Guardar Colección
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setIsAdding(false);
                setFormError(null);
              }}
              className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Tabla */}
      <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-brand-gray-50">
              <tr>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3 w-10">
                  Orden
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Colección
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Año
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Estado
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Imágenes
                </th>
                <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-gray-200">
              {paged.map((col) => (
                <tr
                  key={col.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, col.id)}
                  onDragOver={(e) => handleDragOver(e, col.id)}
                  onDrop={(e) => handleDrop(e, col.id)}
                  onDragEnd={handleDragEnd}
                  className={`hover:bg-brand-gray-50/50 transition-colors cursor-move ${
                    draggingId === col.id ? 'opacity-40' : ''
                  } ${dragOverId === col.id ? 'bg-brand-sage/20' : ''}`}
                >
                  <td className="px-5 py-4">
                    <div className="w-6 h-6 flex items-center justify-center text-brand-gray-400">
                      <i className="ri-draggable text-lg" />
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-brand-gray-100 flex-shrink-0">
                        {col.image ? (
                          <img
                            src={col.image}
                            alt={col.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-brand-gray-400 text-xs">
                            Sin img
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-sans text-brand-black text-sm font-medium">
                          {col.name}
                        </p>
                        <p className="font-sans text-brand-gray-400 text-xs mt-0.5 line-clamp-1 max-w-[200px]">
                          {col.description}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 font-sans text-brand-gray-600 text-sm">
                    {col.year}
                  </td>
                  <td className="px-5 py-4">
                    {col.featured ? (
                      <span className="inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium bg-brand-sage text-brand-black">
                        Destacada
                      </span>
                    ) : (
                      <span className="inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium bg-brand-gray-100 text-brand-gray-500">
                        Normal
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 font-sans text-brand-gray-600 text-sm">
                    {col.images?.length ?? 0} + 1
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditing(col);
                          setIsAdding(false);
                          setFormError(null);
                        }}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                        title="Editar"
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        onClick={() => {
                          if (
                            confirm(
                              `¿Eliminar la colección "${col.name}"?`
                            )
                          ) {
                            remove(col.id);
                          }
                        }}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-10 text-center">
            <p className="font-sans text-brand-gray-400 text-sm">
              No se encontraron colecciones
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white border border-brand-gray-200 rounded-2xl px-5 py-3">
          <div className="font-sans text-brand-gray-500 text-sm">
            Mostrando {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, filtered.length)} de{' '}
            {filtered.length}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg font-sans text-sm flex items-center justify-center transition-colors ${
                  p === safePage
                    ? 'bg-brand-black text-white'
                    : 'border border-brand-gray-200 text-brand-gray-600 hover:border-brand-black hover:text-brand-black'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors disabled:opacity-40"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}